import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Zap, UserCheck, TrendingUp, Sparkles, ArrowRight, Store } from 'lucide-react';
import { LogoIcon } from '@/components/common/Logo';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useOnboardingStore } from '@/stores/useOnboardingStore';
import { useGetApiWorkspacesWorkspaceIdStores } from '@/api/generated/stores/stores';
import { extractStoresArray } from '@/lib/utils';
import type { StoreItem } from '@/types';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';

export const WelcomeModal: React.FC = () => {
  const { activeWorkspaceId } = useWorkspaceStore();
  const {
    hasSeenWelcomeModal,
    isWelcomeModalOpen,
    openWelcomeModal,
    dismissWelcomeModal,
    startTour,
    completeTour,
  } = useOnboardingStore();

  const location = useLocation();
  const navigate = useNavigate();

  const { data: stores } = useGetApiWorkspacesWorkspaceIdStores(activeWorkspaceId!, { query: { enabled: !!activeWorkspaceId } });
  const storesList = extractStoresArray(stores) as StoreItem[];
  const hasConnectedStore = storesList.filter((s) => s.status === 'Connected' || !s.status).length > 0;

  // Auto-open welcome modal on first arrival at conversations page
  useEffect(() => {
    if (!hasSeenWelcomeModal && !isWelcomeModalOpen && location.pathname.startsWith('/conversations')) {
      openWelcomeModal();
    }
  }, [hasSeenWelcomeModal, isWelcomeModalOpen, location.pathname, openWelcomeModal]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      dismissWelcomeModal();
    }
  };

  const handleStartTour = () => {
    startTour();
  };

  const handleExploreAlone = () => {
    completeTour();
  };

  const handleConnectStore = () => {
    completeTour();
    navigate('/integrations');
  };

  return (
    <Dialog open={isWelcomeModalOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px] p-0 overflow-hidden border-border/80 bg-card shadow-2xl">
        {/* Top Decorative Header Banner */}
        <div className="relative p-6 pb-4 bg-gradient-to-br from-primary/15 via-primary/5 to-transparent border-b border-border/40">
          <div className="flex items-center gap-3.5 mb-2.5">
            <div className="h-12 w-12 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center shadow-sm shrink-0">
              <LogoIcon className="h-8 w-8 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-1.5 text-xs font-semibold text-primary uppercase tracking-wider">
                <Sparkles className="h-3.5 w-3.5" />
                <span>{hasConnectedStore ? 'Agente de IA Ativo' : 'Agente de IA para E-commerce'}</span>
              </div>
              <DialogTitle className="text-xl font-bold tracking-tight text-foreground">
                {hasConnectedStore ? 'Sua loja está conectada!' : 'Bem-vindo ao Tales!'}
              </DialogTitle>
            </div>
          </div>
          <DialogDescription className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
            {hasConnectedStore
              ? 'Seu Agente de IA está pronto para atender seus clientes e acelerar as vendas da sua loja.'
              : 'Automatize seu atendimento e aumente as conversões da sua loja com Agentes de IA.'}
          </DialogDescription>
        </div>

        {/* Value Proposition Pillars */}
        <div className="p-6 pt-4 space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {hasConnectedStore ? 'O que você ganha a partir de agora:' : 'Como o Tales acelera suas vendas:'}
          </p>

          {/* Pillar 1 */}
          <div className="flex items-start gap-3 p-3 rounded-xl bg-muted/40 border border-border/50">
            <div className="h-8 w-8 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0 mt-0.5">
              <Zap className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <h5 className="text-xs sm:text-sm font-semibold text-foreground">
                Respostas Instantâneas 24/7
              </h5>
              <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">
                {hasConnectedStore
                  ? 'O Agente de IA consulta o catálogo e tira dúvidas sobre frete, prazos e produtos da sua loja sem filas de espera.'
                  : 'O Agente de IA responde dúvidas de clientes sobre produtos, estoque e frete a qualquer momento sem filas de espera.'}
              </p>
            </div>
          </div>

          {/* Pillar 2 */}
          <div className="flex items-start gap-3 p-3 rounded-xl bg-muted/40 border border-border/50">
            <div className="h-8 w-8 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0 mt-0.5">
              <UserCheck className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <h5 className="text-xs sm:text-sm font-semibold text-foreground">
                Controle Total & Atendimento Humano
              </h5>
              <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">
                Acompanhe as conversas em tempo real e assuma o controle com 1 clique sempre que desejar falar com o cliente.
              </p>
            </div>
          </div>

          {/* Pillar 3 */}
          <div className="flex items-start gap-3 p-3 rounded-xl bg-muted/40 border border-border/50">
            <div className="h-8 w-8 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0 mt-0.5">
              {hasConnectedStore ? <TrendingUp className="h-4 w-4" /> : <Store className="h-4 w-4" />}
            </div>
            <div className="min-w-0 flex-1">
              <h5 className="text-xs sm:text-sm font-semibold text-foreground">
                {hasConnectedStore ? 'Menos Abandono, Mais Vendas' : 'Conecte sua Loja em 1 Minuto'}
              </h5>
              <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">
                {hasConnectedStore
                  ? 'Atendimento rápido no momento da decisão reduz dúvidas e aumenta a conversão de pedidos do seu e-commerce.'
                  : 'Vincule sua plataforma de e-commerce para a IA sincronizar catálogo, preços e pedidos em tempo real.'}
              </p>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <DialogFooter className="p-4 px-6 bg-muted/20 border-t border-border/60 flex flex-col-reverse sm:flex-row items-center justify-between gap-2.5">
          {hasConnectedStore ? (
            <>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleExploreAlone}
                className="w-full sm:w-auto text-xs text-muted-foreground hover:text-foreground"
              >
                Explorar Sozinho
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handleStartTour}
                className="w-full sm:w-auto text-xs font-semibold gap-1.5 shadow-sm"
              >
                <span>Fazer Tour Rápido (1 min)</span>
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleStartTour}
                className="w-full sm:w-auto text-xs text-muted-foreground hover:text-foreground"
              >
                Ver Tour com Exemplo (1 min)
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handleConnectStore}
                className="w-full sm:w-auto text-xs font-semibold gap-1.5 shadow-sm"
              >
                <Store className="h-3.5 w-3.5" />
                <span>Conectar Minha Loja</span>
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
